import { ApplicationFile } from '../Application/application';
import { ApplicationSigningType } from '../Enums/ApplicationSigningType';
import { ForaProductCoverages } from '../Fora/Fora';
import { CheckboxElementBlock } from '../global';

export enum ApplicationTypeEnum {
  // Keep numeric values aligned with backend enum where applicable.
  OngoingEmployment = 0,
  EndedFixedTermEmployment = 1,
  DismissedDueToSickness = 2,
  // Frontend-specific values without direct backend equivalents.
  DismissedFromPermanentEmployment = 3,
  CompletionForApplication = 4,
}

export enum AddressCountryEnum {
  Sweden = 'SE',
  Other = 'OTHER',
}

export enum FormOfEmploymentEnum {
  PermanentEmployee = 866190000,
  TemporaryEmployee = 866190001,
}

export enum ButtonActionEnum {
  Auto = 'auto',
  Manual = 'manual',
  ManualCsn = 'manualCsn',
  ManualCompletion = 'manualCompletion',
}

export enum ApplicationSteps {
  Start = 1,
  Identified = 2,
  Form = 3,
  Confirmation = 4,
}

export enum NonQualifyingPeriodTypeEnum {
  SickLeave = 3,
  TotalDefenseDuty = 5,
  ParentalLeave = 6,
  StudiesWithAdjustmentAid = 7,
}

export const NonQualifyingPeriodText: {
  [key in NonQualifyingPeriodTypeEnum]: string;
} = {
  [NonQualifyingPeriodTypeEnum.ParentalLeave]: 'Föräldraledighet',
  [NonQualifyingPeriodTypeEnum.SickLeave]: 'Sjukskriven',
  [NonQualifyingPeriodTypeEnum.TotalDefenseDuty]: 'Totalförsvarsplikt',
  [NonQualifyingPeriodTypeEnum.StudiesWithAdjustmentAid]:
    'Studier med omställningsstöd',
};

export type ApplicationType =
  (typeof ApplicationTypeEnum)[keyof typeof ApplicationTypeEnum];

export enum ConfirmEmploymentEnum {
  Yes = 'yes',
  No = 'no',
}
export type ConfirmEmploymentType =
  (typeof ConfirmEmploymentEnum)[keyof typeof ConfirmEmploymentEnum];

export type NonQualifyingPeriodType =
  (typeof NonQualifyingPeriodTypeEnum)[keyof typeof NonQualifyingPeriodTypeEnum];

export interface EmploymentPeriodView {
  id: string;
  organizationNumber: string;
  organizationName: string;
  isNonQualifyingPeriod?: boolean;
  isLatestEmployment?: boolean;
  retrievedFromApplication?: boolean;
  isCsnCompletionEmployment?: boolean;
  hasEditedForaDates?: boolean;
  hasPayslipToProveOngoingEmployment?: boolean;
  hasWorked16hConfirmation?: boolean;
  comment?: string;
  sicknessAttachments?: ApplicationFile[];
  employmentCertificates?: ApplicationFile[];
  workedTimeEmploymentCertificates?: ApplicationFile[];
  latestPayslipAttachments?: ApplicationFile[];
  startDate: string;
  noticeDate?: string;
  endDate: string;
  temporaryEmploymentHasExpired: boolean;
  worksite?: Worksite;
  worksiteContactDetails?: WorksiteContactDetails;
  retrievedFromFora: boolean;
  workedMonths: WorkedMonth[];
  agreement?: ForaProductCoverages;
}

export interface WorksiteContactDetails {
  firstname: string;
  lastname: string;
  phone: string;
  email: string;
}

export interface Worksite {
  id: string;
  name: string;
  address: string;
  country?: AddressCountryEnum;
  city: string;
  zipCode?: string;
  telephone?: string;
  didNotFindWorksiteFromBisnode?: boolean;
}

export interface NonQualifyingPeriodView {
  id: string;
  nonQualifyingPeriodType: NonQualifyingPeriodType;
  startDate: string;
  endDate: string;
  isOngoing: boolean;
  attachments: ApplicationFile[];
  hasAttachmentFromPreviousApplication?: boolean;
}

export interface WorkedMonth {
  year: number;
  month: number;
  hasPayroll: boolean;
  meetsSalaryRequirement?: boolean;
  meetsSalaryRequirementWithAnotherEmployment?: boolean;
  totalPremiumBasedPayAmount?: number;
  attachments?: ApplicationFile[];
  hasAttachmentFromPreviousApplication?: boolean;
}

export interface EmployerInfo {
  id: string;
  name: string;
  note?: string;
  warning?: string;
  error?: string;
  attachments: ApplicationFile[];
  meetsSalaryRequirement?: boolean;
  meetsSalaryRequirementWithAnotherEmployment?: boolean;
  hasConfirmed16Hours?: boolean;
  hasAttachment?: boolean;
  hasAttachmentFromPreviousApplication?: boolean;
  hasWorkedTimeEmploymentCertificate?: boolean;
  retrievedFromFora?: boolean;
  isNonQualifyingPeriod?: boolean;
  nonQualifyingPeriodType?: NonQualifyingPeriodType;
  period?: {
    start: string;
    end: string;
  };
}

export interface MonthData {
  id: string;
  monthLabel: string;
  year: number;
  month: number;
  employers: EmployerInfo[];
}

export interface ContactDetails {
  socialSecurityNumber: string;
  firstname: string;
  lastname: string;
  address: string;
  isSwedishAddress: boolean;
  zipCode: string;
  city: string;
  coAddress: string;
  phone: string;
  email: string;
  protectedIdentity?: boolean;
  retrievedFromBisnode?: boolean;
  gedi?: string;
}

export interface ApplicationModel {
  incidentId: string;
  tslNumber?: string;
  applicationType: ApplicationType;
  applicationTypeName: string;
  formOfEmployment?: FormOfEmploymentEnum;
  employmentPeriods: EmploymentPeriodView[];
  nonQualifyingPeriods: NonQualifyingPeriodView[];
  listOfConsents: CheckboxElementBlock[];
  applicantContactDetails: ContactDetails;
  monthDataList: MonthData[];
  otherInformation?: string;
  signingType: ApplicationSigningType;
  signedApplicationDocument?: ApplicationFile;
  incompleteApplication: boolean;
}

export interface ApplicationDocumentModel {
  applicationType: ApplicationType;
  applicationTypeName: string;
  employmentPeriods: EmploymentPeriodView[];
  nonQualifyingPeriods: NonQualifyingPeriodView[];
  listOfConsents: CheckboxElementBlock[];
  applicantContactDetails: ContactDetails;
  otherInformation?: string;
}

export enum LatestIncidentBySsnMatchOutcome {
  NotFound = 0,
  OpenAndWaitingForCompletion = 1,
  OpenUnderProcessing = 2,
  ClosedCaseFound = 3,
}
